/**
 * Reading a GLB into buffers WebGL can draw.
 *
 * A deliberately small parser, for the same reason `ply.ts` is one: this needs
 * positions, indices and normals out of a container the server already
 * validated. A glTF loader brings materials, animation, skinning, extensions,
 * KTX textures and Draco - none of which a first mesh viewer draws.
 *
 * **It refuses the same things the server refuses**, and that duplication is
 * intentional. The backend's `validate_glb` is the authority and no artifact is
 * registered without passing it; this is the second wall, so a file that
 * reached the browser some other way still cannot be presented as a model. The
 * one that matters: a primitive in POINTS mode is a point cloud, it renders
 * perfectly well, and it is not a mesh.
 *
 * Normals are read when present and computed when not. A mesh with no normals
 * renders as a flat silhouette under any lighting, which looks exactly like a
 * reconstruction failure - so the geometric normal per face is derived rather
 * than shipping a viewer that makes good models look broken.
 *
 * `_PROVENANCE` is read where the mesh stage wrote it: one unsigned byte per
 * vertex saying whether a camera measured that part of the surface. It is an
 * application-specific attribute - the leading underscore is glTF's own
 * convention for that - so a file carrying it still opens anywhere. A model
 * without it comes back with `provenance: null`, which the viewer draws as
 * *unverified* and never as observed: the whole point of the attribute is that
 * its absence is a real answer.
 */

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;

const MODE_TRIANGLES = 4;

/**
 * The wire codes in `_PROVENANCE`, matching `reconstruction/provenance.CODES`.
 *
 * Numbers rather than names because this is a byte in a binary buffer, and
 * they are a stable contract in both directions: renumbering one would
 * re-colour every model already delivered the next time somebody opened it.
 */
export const SURFACE_PROVENANCE_BY_CODE = [
  "observed",
  "weakly_observed",
  "ai_completed",
  "unverified",
] as const;

/** What an unlabelled vertex is. Not "observed" - see `Mesh.provenance`. */
const UNVERIFIED_CODE = 3;

/** glTF component types this parser reads. */
const COMPONENT = {
  5121: { array: Uint8Array, size: 1 },
  5123: { array: Uint16Array, size: 2 },
  5125: { array: Uint32Array, size: 4 },
  5126: { array: Float32Array, size: 4 },
} as const;

/**
 * What one unit of each component type means as a colour.
 *
 * glTF stores vertex colour as float in 0..1, or as a normalised integer that
 * has to be divided by its own maximum. Getting this wrong does not fail - it
 * draws a white model, because every channel saturates.
 */
const COLOUR_SCALE: Record<number, number> = {
  5121: 1 / 255,
  5123: 1 / 65535,
  5126: 1,
};

const COMPONENTS_PER_TYPE: Record<string, number> = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
};

export interface Mesh {
  /** Interleaved-free: xyz per vertex. */
  positions: Float32Array;
  /** One per vertex, unit length. Read or derived. */
  normals: Float32Array;
  /**
   * Linear RGB per vertex, or null when the file carries none.
   *
   * Null and grey are different answers and the viewer treats them so. This
   * pipeline's GLB is vertex-coloured and carries no texture, so colour is the
   * only surface appearance there is - drawing it untinted would not be a
   * neutral default, it would be discarding the photometry the dense stage
   * spent its whole runtime measuring.
   */
  colors: Float32Array | null;
  indices: Uint32Array;
  vertexCount: number;
  faceCount: number;
  /**
   * Texture coordinates, or null when nothing in the file is textured.
   *
   * Kept aligned with `positions` even when only some primitives carry them:
   * an untextured primitive contributes zeroes rather than shifting every
   * vertex after it, which is the same reasoning `colors` uses.
   */
  uvs: Float32Array | null;
  /**
   * Where each vertex's surface came from, or null when the file says nothing.
   *
   * Values are the codes in `SURFACE_PROVENANCE_BY_CODE`. Null means the model
   * predates the measurement or the pipeline could not make it, and it is
   * *not* interchangeable with an array of zeroes - zero is "observed", which
   * is the one claim a file that carries no evidence must not be made to make.
   */
  provenance: Uint8Array | null;
  /** Encoded atlas bytes, in file order. Empty for an untextured model. */
  textures: MeshTexture[];
  /**
   * Contiguous runs of `indices`, one per primitive, with the atlas each is
   * drawn with. Null when nothing is textured, which is what keeps every
   * model this product has already shipped on the single-draw path it was
   * written for.
   */
  groups: MeshGroup[] | null;
}

/** One atlas, still encoded - the browser decodes it, not this parser. */
export interface MeshTexture {
  data: Uint8Array;
  mimeType: string;
}

/** A run of triangles sharing one atlas. */
export interface MeshGroup {
  /** Offset into `indices`, in elements. */
  start: number;
  count: number;
  /** Index into `textures`, or null for an untextured run. */
  texture: number | null;
}

export class MeshParseError extends Error {}

function accessorData(
  document: Record<string, unknown>,
  binary: DataView,
  index: number,
): {
  values: ArrayLike<number>;
  count: number;
  components: number;
  componentType: number;
  normalized: boolean;
} {
  const accessors = (document.accessors as Record<string, unknown>[]) ?? [];
  const views = (document.bufferViews as Record<string, unknown>[]) ?? [];
  const accessor = accessors[index];
  if (!accessor) throw new MeshParseError("model references a missing accessor");

  const spec = COMPONENT[accessor.componentType as keyof typeof COMPONENT];
  if (!spec) throw new MeshParseError("model uses an unsupported component type");

  const components = COMPONENTS_PER_TYPE[accessor.type as string] ?? 0;
  if (components === 0) throw new MeshParseError("model uses an unsupported accessor type");

  const count = Number(accessor.count ?? 0);
  if (count <= 0) throw new MeshParseError("model accessor declares no elements");

  const view = views[Number(accessor.bufferView ?? -1)];
  if (!view) throw new MeshParseError("model references a missing buffer view");

  const base =
    binary.byteOffset +
    Number(view.byteOffset ?? 0) +
    Number(accessor.byteOffset ?? 0);

  // `byteStride` is legal and this parser does not interleave; refusing is
  // honest, where reading it as tightly packed would silently draw nonsense.
  const stride = Number(view.byteStride ?? 0);
  if (stride !== 0 && stride !== spec.size * components) {
    throw new MeshParseError("model uses interleaved buffers");
  }

  // `buffer` is typed `ArrayBufferLike` to allow SharedArrayBuffer; a GLB
  // read through fetch never is one.
  const values = new spec.array(binary.buffer as ArrayBuffer, base, count * components);
  return {
    values,
    count,
    components,
    componentType: Number(accessor.componentType),
    normalized: accessor.normalized === true,
  };
}

/**
 * Per-face geometric normals, averaged onto vertices.
 *
 * Used only when the file has none. Smooth-shaded output from an averaged
 * normal is closer to what a photogrammetry mesh should look like than flat
 * faceting, and either is far better than the unlit silhouette a viewer draws
 * with no normals at all.
 */
function deriveNormals(positions: Float32Array, indices: Uint32Array): Float32Array {
  const normals = new Float32Array(positions.length);

  for (let i = 0; i + 2 < indices.length; i += 3) {
    const [a, b, c] = [indices[i]! * 3, indices[i + 1]! * 3, indices[i + 2]! * 3];
    const ux = positions[b]! - positions[a]!;
    const uy = positions[b + 1]! - positions[a + 1]!;
    const uz = positions[b + 2]! - positions[a + 2]!;
    const vx = positions[c]! - positions[a]!;
    const vy = positions[c + 1]! - positions[a + 1]!;
    const vz = positions[c + 2]! - positions[a + 2]!;

    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;

    for (const offset of [a, b, c]) {
      normals[offset] = normals[offset]! + nx;
      normals[offset + 1] = normals[offset + 1]! + ny;
      normals[offset + 2] = normals[offset + 2]! + nz;
    }
  }

  for (let i = 0; i < normals.length; i += 3) {
    const x = normals[i]!;
    const y = normals[i + 1]!;
    const z = normals[i + 2]!;
    const length = Math.hypot(x, y, z);
    if (length > 0) {
      normals[i] = x / length;
      normals[i + 1] = y / length;
      normals[i + 2] = z / length;
    } else {
      // A degenerate triangle contributed nothing. Point it somewhere valid
      // rather than leaving a zero vector, which shades as a black hole.
      normals[i + 1] = 1;
    }
  }
  return normals;
}

/**
 * Parse a GLB into drawable geometry.
 *
 * @throws MeshParseError when the bytes are not a GLB, or are a GLB with no
 *   triangulated surface in it.
 */
export function parseGlb(buffer: ArrayBuffer): Mesh {
  if (buffer.byteLength < 20) throw new MeshParseError("file is too short to be a model");

  const header = new DataView(buffer);
  if (header.getUint32(0, true) !== GLB_MAGIC) {
    throw new MeshParseError("file is not a GLB model");
  }
  if (header.getUint32(4, true) !== 2) {
    throw new MeshParseError("unsupported GLB version");
  }
  if (header.getUint32(8, true) !== buffer.byteLength) {
    throw new MeshParseError("model file is incomplete");
  }

  let document: Record<string, unknown> | null = null;
  let binary: DataView | null = null;

  let offset = 12;
  while (offset + 8 <= buffer.byteLength) {
    const length = header.getUint32(offset, true);
    const type = header.getUint32(offset + 4, true);
    const body = offset + 8;
    if (body + length > buffer.byteLength) {
      throw new MeshParseError("model file is incomplete");
    }
    if (type === CHUNK_JSON && document === null) {
      const text = new TextDecoder().decode(new Uint8Array(buffer, body, length));
      try {
        document = JSON.parse(text) as Record<string, unknown>;
      } catch {
        throw new MeshParseError("model metadata could not be read");
      }
    } else if (type === CHUNK_BIN && binary === null) {
      binary = new DataView(buffer, body, length);
    }
    offset = body + length + ((4 - (length % 4)) % 4);
  }

  if (document === null) throw new MeshParseError("model contains no metadata");
  if (binary === null) throw new MeshParseError("model contains no geometry");

  const meshes = (document.meshes as Record<string, unknown>[]) ?? [];
  if (meshes.length === 0) throw new MeshParseError("model contains no mesh");

  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const uvs: number[] = [];
  const provenance: number[] = [];
  const indices: number[] = [];
  const textures: MeshTexture[] = [];
  const groups: MeshGroup[] = [];
  //: Which `textures` entry an image index already produced, so two materials
  //: pointing at one atlas upload it once.
  const textureByImage = new Map<number, number>();
  let sawSurface = false;
  let sawColour = false;
  let sawTexture = false;
  let sawProvenance = false;

  for (const mesh of meshes) {
    for (const primitive of (mesh.primitives as Record<string, unknown>[]) ?? []) {
      const mode = Number(primitive.mode ?? MODE_TRIANGLES);
      const attributes = (primitive.attributes as Record<string, number>) ?? {};
      if (mode !== MODE_TRIANGLES) continue;
      if (attributes.POSITION === undefined) continue;
      if (primitive.indices === undefined) continue;

      sawSurface = true;
      const base = positions.length / 3;

      const position = accessorData(document, binary, attributes.POSITION);
      for (let i = 0; i < position.values.length; i += 1) {
        positions.push(position.values[i] as number);
      }

      if (attributes.NORMAL !== undefined) {
        const normal = accessorData(document, binary, attributes.NORMAL);
        for (let i = 0; i < normal.values.length; i += 1) {
          normals.push(normal.values[i] as number);
        }
      }

      const vertices = position.count;
      if (attributes.COLOR_0 !== undefined) {
        const colour = accessorData(document, binary, attributes.COLOR_0);
        sawColour = true;
        // glTF allows RGB or RGBA, and float or normalised integers. Alpha is
        // dropped rather than honoured: this mesh is opaque, and a viewer that
        // blended on it would hide holes instead of showing them.
        const scale = COLOUR_SCALE[colour.componentType] ?? 1;
        for (let v = 0; v < vertices; v += 1) {
          const at = v * colour.components;
          colors.push(
            ((colour.values[at] as number) ?? 1) * scale,
            ((colour.values[at + 1] as number) ?? 1) * scale,
            ((colour.values[at + 2] as number) ?? 1) * scale,
          );
        }
      } else {
        // Keeps the colour array aligned with positions when one primitive
        // carries colour and another does not. White is the identity tint, so
        // an uncoloured primitive beside a coloured one draws as lit geometry
        // rather than shifting every index after it.
        for (let v = 0; v < vertices; v += 1) colors.push(1, 1, 1);
      }

      if (attributes._PROVENANCE !== undefined) {
        const codes = accessorData(document, binary, attributes._PROVENANCE);
        sawProvenance = true;
        for (let v = 0; v < vertices; v += 1) {
          provenance.push((codes.values[v * codes.components] as number) ?? UNVERIFIED_CODE);
        }
      } else {
        // A primitive with no provenance beside one that has it is unverified,
        // not observed. Padding with zero here would label an unmeasured run
        // of the surface as measured, which is the exact mistake the whole
        // attribute exists to make impossible.
        for (let v = 0; v < vertices; v += 1) provenance.push(UNVERIFIED_CODE);
      }

      if (attributes.TEXCOORD_0 !== undefined) {
        const uv = accessorData(document, binary, attributes.TEXCOORD_0);
        sawTexture = true;
        for (let v = 0; v < vertices; v += 1) {
          const at = v * uv.components;
          uvs.push((uv.values[at] as number) ?? 0, (uv.values[at + 1] as number) ?? 0);
        }
      } else {
        for (let v = 0; v < vertices; v += 1) uvs.push(0, 0);
      }

      const start = indices.length;
      const index = accessorData(document, binary, Number(primitive.indices));
      for (let i = 0; i < index.count; i += 1) {
        indices.push(base + (index.values[i] as number));
      }

      groups.push({
        start,
        count: index.count,
        texture: textureFor(document, binary, primitive, textures, textureByImage),
      });
    }
  }

  if (!sawSurface) {
    // The refusal this parser exists for: a valid GLB whose primitives are
    // points renders fine and is not a model.
    throw new MeshParseError(
      "model contains no triangulated surface",
    );
  }
  if (indices.length === 0) throw new MeshParseError("model contains no faces");

  const textured = sawTexture && textures.length > 0;
  const positionArray = new Float32Array(positions);
  const indexArray = new Uint32Array(indices);
  const normalArray =
    normals.length === positions.length
      ? new Float32Array(normals)
      : deriveNormals(positionArray, indexArray);

  return {
    positions: positionArray,
    normals: normalArray,
    colors:
      sawColour && colors.length === positions.length ? new Float32Array(colors) : null,
    indices: indexArray,
    vertexCount: positionArray.length / 3,
    faceCount: indexArray.length / 3,
    uvs: textured ? new Float32Array(uvs) : null,
    provenance:
      sawProvenance && provenance.length === positionArray.length / 3
        ? Uint8Array.from(provenance)
        : null,
    textures,
    // Null rather than a single all-null group, so an untextured model takes
    // exactly the path it took before this existed.
    groups: textured ? groups : null,
  };
}

/**
 * The atlas a primitive is drawn with, uploaded once per image.
 *
 * Walks material -> baseColorTexture -> texture -> image -> bufferView, and
 * returns null at the first step that does not lead anywhere. A break in that
 * chain is a file the server should already have refused; returning null here
 * means the geometry still draws, untextured, rather than the whole model
 * failing to open on a bad picture.
 */
function textureFor(
  document: Record<string, unknown>,
  binary: DataView,
  primitive: Record<string, unknown>,
  textures: MeshTexture[],
  seen: Map<number, number>,
): number | null {
  const materials = (document.materials as Record<string, unknown>[]) ?? [];
  const declared = (document.textures as Record<string, unknown>[]) ?? [];
  const images = (document.images as Record<string, unknown>[]) ?? [];
  const views = (document.bufferViews as Record<string, number>[]) ?? [];

  const materialIndex = primitive.material;
  if (typeof materialIndex !== "number") return null;
  const material = materials[materialIndex];
  if (!material) return null;
  const pbr = (material.pbrMetallicRoughness as Record<string, unknown>) ?? {};
  const slot = pbr.baseColorTexture as Record<string, number> | undefined;
  if (!slot || typeof slot.index !== "number") return null;
  const texture = declared[slot.index];
  if (!texture) return null;
  const source = texture.source;
  if (typeof source !== "number") return null;

  const already = seen.get(source);
  if (already !== undefined) return already;

  const image = images[source];
  if (!image) return null;
  const viewIndex = image.bufferView;
  if (typeof viewIndex !== "number") return null;
  const view = views[viewIndex];
  if (!view) return null;
  const offset = Number(view.byteOffset ?? 0);
  const length = Number(view.byteLength ?? 0);
  if (length <= 0 || offset + length > binary.byteLength) return null;

  textures.push({
    // A view onto the chunk rather than a copy: an atlas is megabytes, and the
    // browser is about to hand it straight to `createImageBitmap`.
    data: new Uint8Array(binary.buffer as ArrayBuffer, binary.byteOffset + offset, length),
    mimeType: String(image.mimeType ?? "image/png"),
  });
  const at = textures.length - 1;
  seen.set(source, at);
  return at;
}

/** Axis-aligned bounds, for fitting a camera to whatever this actually is. */
export function meshBounds(mesh: Mesh): {
  center: [number, number, number];
  radius: number;
} {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];

  for (let i = 0; i < mesh.positions.length; i += 3) {
    for (let axis = 0; axis < 3; axis += 1) {
      const value = mesh.positions[i + axis]!;
      if (value < min[axis]!) min[axis] = value;
      if (value > max[axis]!) max[axis] = value;
    }
  }

  const center: [number, number, number] = [
    (min[0]! + max[0]!) / 2,
    (min[1]! + max[1]!) / 2,
    (min[2]! + max[2]!) / 2,
  ];
  // From the real extent, so this fits a head, an object or a room without
  // any of them being assumed.
  const radius =
    Math.max(max[0]! - min[0]!, max[1]! - min[1]!, max[2]! - min[2]!) / 2 || 1;

  return { center, radius };
}
